import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getUserBadgeDefinitions, getUserBadgeIds } from '../../config/configUserBadges';
import { useIntl } from '../../util/reactIntl';

import css from './UserBadgeList.module.css';

const BadgeIcon = ({ icon, className }) => {
  if (icon === 'award-star') {
    return (
      <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
        <path d="m10 2 2 2.1 2.9-.2.2 2.9 2.1 2-2.1 2 .2 2.9-2.9-.2-2 2.1-2-2.1-2.9.2.2-2.9-2.1-2 2.1-2-.2-2.9 2.9.2L10 2Z" />
      </svg>
    );
  }

  if (icon === 'fast-parcel') {
    return (
      <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
        <path d="M3 6.5 10 3l7 3.5v7L10 17l-7-3.5v-7Zm7 2.8 4.5-2.2L10 4.8 5.5 7.1 10 9.3Zm-1 5.2v-3.4L5 9.2v3.7l4 2Zm2 0 4-2V9.2l-4 1.9v3.4Z" />
        <path d="M1 4h5v1.5H1zM0 7h5v1.5H0z" />
      </svg>
    );
  }

  if (icon === 'heart-book') {
    return (
      <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
        <path d="M3 3.5h5.2c.7 0 1.3.2 1.8.7.5-.5 1.1-.7 1.8-.7H17v12h-5.2c-.5 0-.9.2-1.2.5l-.6.6-.6-.6c-.3-.3-.7-.5-1.2-.5H3v-12Zm2 2v8h3.2c.3 0 .5 0 .8.1V6c-.2-.3-.5-.5-.8-.5H5Zm10 8v-8h-3.2c-.3 0-.6.2-.8.5v7.6c.3-.1.5-.1.8-.1H15Z" />
        <path d="M10 11.8 7.7 9.6a1.5 1.5 0 0 1 2.1-2.1l.2.2.2-.2a1.5 1.5 0 1 1 2.1 2.1L10 11.8Z" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3 4h5.2c.7 0 1.3.2 1.8.7.5-.5 1.1-.7 1.8-.7H17v11.5h-5.2c-.5 0-.9.2-1.2.5l-.6.6-.6-.6c-.3-.3-.7-.5-1.2-.5H3V4Zm2 2v7.5h3.2c.3 0 .5 0 .8.1V6.5c-.2-.3-.5-.5-.8-.5H5Zm10 7.5V6h-3.2c-.3 0-.6.2-.8.5v7.1c.3-.1.5-.1.8-.1H15Z" />
      <path d="m15.5 1 .6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4Z" />
    </svg>
  );
};

/**
 * Render public PageHop user badges from persisted profile metadata or explicit fixture IDs.
 */
const UserBadgeList = props => {
  const { user, badgeIds, className, compact = false } = props;
  const intl = useIntl();
  const persistedBadgeIds = badgeIds === undefined ? getUserBadgeIds(user) : badgeIds;
  const badges = getUserBadgeDefinitions(persistedBadgeIds);

  if (badges.length === 0) {
    return null;
  }

  return (
    <ul
      className={classNames(css.root, { [css.compact]: compact }, className)}
      aria-label={intl.formatMessage({ id: 'UserBadgeList.ariaLabel' })}
    >
      {badges.map(badge => {
        const label = intl.formatMessage({ id: badge.labelMessageId, defaultMessage: badge.label });
        const description = intl.formatMessage({
          id: badge.descriptionMessageId,
          defaultMessage: badge.description,
        });
        return (
          <li
            key={badge.id}
            className={classNames(css.badge, css[badge.variant], { [css.compactBadge]: compact })}
            title={description}
            data-badge-id={badge.id}
          >
            <span className={classNames(css.icon, { [css.compactIcon]: compact })}>
              <BadgeIcon className={css.svg} icon={badge.icon} />
            </span>
            <span className={css.label}>{label}</span>
          </li>
        );
      })}
    </ul>
  );
};

UserBadgeList.propTypes = {
  user: PropTypes.object,
  badgeIds: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  compact: PropTypes.bool,
};

export default UserBadgeList;